import { useEffect, useMemo, useRef, useState } from "react";

// SearchableSelect — a type-to-filter combobox for the admin app's long pickers
// (LSC-371). Legend/user/cave assignment lists can run to hundreds of entries
// (196 seeded legends alone), and a bare <select> makes the operator scroll to
// find one. This renders a labeled text input that filters an options list the
// operator clicks — no dependency, admin-scoped styling (used under .admin-scope).
//
// It is intentionally small: click-to-pick + Enter-picks-the-first-match +
// Escape-closes, options filtered by a case-insensitive substring of the label.
// Full arrow-key roving is a later refinement; the win here is not scrolling a
// 200-row native dropdown.

export interface SearchableOption {
  value: string;
  label: string;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  ariaLabel,
  placeholder = "Type to search…",
  disabled = false,
}: {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Close when focus leaves the whole control (click outside / tab away).
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  const pick = (opt: SearchableOption) => {
    onChange(opt.value);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <input
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        // When closed, show the current selection; when open, show what's being typed.
        value={open ? query : selected?.label ?? ""}
        placeholder={selected ? selected.label : placeholder}
        onFocus={() => {
          if (disabled) return;
          setQuery("");
          setOpen(true);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "Enter") {
            // Enter picks the single/first visible match — the fast path once a
            // few characters have narrowed the list to what the operator wants.
            if (open && filtered.length > 0) {
              e.preventDefault();
              pick(filtered[0]);
            }
          }
        }}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "8px 10px",
          background: "var(--surface-1)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-control, 6px)",
          color: "var(--text-1)",
          font: "inherit",
          fontSize: "var(--fs-sm)",
        }}
      />
      {open && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          style={{
            position: "absolute",
            zIndex: 20,
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            margin: 0,
            padding: 4,
            listStyle: "none",
            maxHeight: 240,
            overflowY: "auto",
            background: "var(--surface-1)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-control, 6px)",
            boxShadow: "var(--shadow-1, 0 6px 20px rgba(0,0,0,.35))",
          }}
        >
          {filtered.length === 0 ? (
            <li style={{ padding: "8px 10px", fontSize: "var(--fs-sm)", color: "var(--text-3)" }}>
              No matches
            </li>
          ) : (
            filtered.map((o) => (
              <li key={o.value} role="option" aria-selected={o.value === value}>
                <button
                  type="button"
                  // mousedown fires before the input's blur, so the pick lands.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(o);
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "7px 10px",
                    background: o.value === value ? "var(--surface-2)" : "transparent",
                    border: "none",
                    borderRadius: "var(--radius-control, 6px)",
                    color: "var(--text-1)",
                    font: "inherit",
                    fontSize: "var(--fs-sm)",
                    cursor: "pointer",
                  }}
                >
                  {o.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
